var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable camelcase, @typescript-eslint/naming-convention */
import orchestratorApiClient, { getOrchestratorBaseUrl } from './orchestrator-api-client';
// ---------------------------------------------------------------------------
// API Methods
// ---------------------------------------------------------------------------
const getFilesBaseUrl = () => {
    return `${getOrchestratorBaseUrl()}/files`;
};
/**
 * Request a presigned upload URL from the orchestrator.
 * POST /api/files/upload-url
 */
export function requestUploadUrl(filename, contentType, fileSize) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.post(`${getFilesBaseUrl()}/upload-url`, {
                file_name: filename,
                content_type: contentType,
                file_size_bytes: fileSize
            }, { baseURL: '' });
            return response.data;
        }
        catch (error) {
            console.error('[uploadApi] Error requesting upload URL:', error);
            throw error;
        }
    });
}
/**
 * Upload a file directly to S3 using a presigned URL.
 * PUT to presigned URL → 200
 */
export function uploadToS3(uploadUrl, file, onProgress, contentTypeOverride) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open('PUT', uploadUrl, true);
            // The presigned URL bakes in the Content-Type — caller passes the same
            // value used in the presign request so the signature matches. Without
            // this override, browsers that report empty file.type (e.g. .md on
            // macOS) send an empty Content-Type header and S3 returns 403.
            xhr.setRequestHeader('Content-Type', contentTypeOverride || file.type);
            if (onProgress) {
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        const pct = Math.round((event.loaded / event.total) * 100);
                        onProgress(pct);
                    }
                });
            }
            xhr.addEventListener('load', () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    resolve();
                }
                else {
                    reject(new Error(`S3 upload failed with status ${xhr.status}`));
                }
            });
            xhr.addEventListener('error', () => {
                reject(new Error('S3 upload failed due to network error'));
            });
            xhr.addEventListener('abort', () => {
                reject(new Error('S3 upload was aborted'));
            });
            xhr.send(file);
        });
    });
}
/**
 * Confirm an upload after S3 PUT completes.
 * POST /api/files/confirm
 */
export function confirmUpload(fileId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.post(`${getFilesBaseUrl()}/confirm`, { file_id: fileId }, { baseURL: '' });
            return response.data;
        }
        catch (error) {
            console.error('[uploadApi] Error confirming upload:', error);
            throw error;
        }
    });
}
/**
 * Check scan status for an uploaded file.
 * GET /api/files/{file_id}/status
 */
export function getScanStatus(fileId) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.get(`${getFilesBaseUrl()}/${fileId}/status`, {
                baseURL: ''
            });
            return response.data;
        }
        catch (error) {
            console.error('[uploadApi] Error checking scan status:', error);
            throw error;
        }
    });
}
/**
 * Mint a fresh presigned download URL for a download whose previous one expired.
 * POST /api/files/{ref}/refresh-url  body: { source }
 *
 * `ref` is the block's `download_ref`, and `source` says which backend owns it:
 * an orchestrator generated-file id, or a Rails CSV export id. The two are
 * different id spaces, so the source has to travel with the ref.
 */
export function refreshFileDownloadUrl(ref, source) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const response = yield orchestratorApiClient.post(`${getFilesBaseUrl()}/${encodeURIComponent(ref)}/refresh-url`, { source }, { baseURL: '' });
            return response.data;
        }
        catch (error) {
            console.error('[uploadApi] Error refreshing download URL:', error);
            throw error;
        }
    });
}
